// api.js — axios client with JWT interceptor
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && !window.location.pathname.startsWith("/login")
        && !window.location.pathname.startsWith("/register")) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      // soft redirect
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg || JSON.stringify(e))).join(" · ");
  if (typeof d === "object" && d.msg) return d.msg;
  return JSON.stringify(d);
}

// ---- Auth ----
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (payload) => api.post("/auth/register", payload),
  me: () => api.get("/auth/me"),
  logout: () => api.post("/auth/logout"),
};

// ---- Borrowers ----
export const borrowersApi = {
  list: (params) => api.get("/borrowers", { params }),
  get: (id) => api.get(`/borrowers/${id}`),
  create: (b) => api.post("/borrowers", b),
  update: (id, b) => api.patch(`/borrowers/${id}`, b),
  remove: (id) => api.delete(`/borrowers/${id}`),
};

// ---- Collaterals ----
export const collateralsApi = {
  list: (params) => api.get("/collaterals", { params }),
  get: (id) => api.get(`/collaterals/${id}`),
  create: (c) => api.post("/collaterals", c),
  update: (id, c) => api.patch(`/collaterals/${id}`, c),
  remove: (id) => api.delete(`/collaterals/${id}`),
  geocode: (pin_code) => api.post("/collaterals/geocode", { pin_code }),
};

// ---- Hazards ----
export const hazardsApi = {
  list: () => api.get("/hazards"),
  zones: (hid, scenario = "current") => api.get(`/hazards/${hid}/zones`, { params: { scenario } }),
};

// ---- Risk ----
export const riskApi = {
  assess: (payload) => api.post("/risk/assess", payload),
  collateral: (id, scenario = "current") =>
    api.post(`/risk/collateral/${id}`, null, { params: { scenario } }),
  collateralScores: (id) => api.get(`/risk/collateral/${id}`),
  rescorePortfolio: (scenario = "current", portfolio) =>
    api.post("/risk/portfolio", null, { params: { scenario, portfolio } }),
  cachedPortfolio: (scenario = "current", portfolio) =>
    api.get("/risk/portfolio", { params: { scenario, portfolio } }),
  borrower: (id, scenario = "current") =>
    api.get(`/risk/portfolio/borrower/${id}`, { params: { scenario } }),
};

// ---- Origination ----
export const originationApi = {
  listTemplates: (stage) => api.get("/origination/templates", { params: { stage } }),
  createTemplate: (t) => api.post("/origination/templates", t),
  listAssessments: (borrower_id) => api.get("/origination/assessments", { params: { borrower_id } }),
  createAssessment: (a) => api.post("/origination/assessments", a),
  getAssessment: (id) => api.get(`/origination/assessments/${id}`),
};

// ---- Signals & KPIs ----
export const signalsApi = {
  signals: (lat, lon) => api.get("/climate/signals", { params: { lat, lon } }),
};
export const dashboardApi = {
  kpis: (scenario = "current") => api.get("/dashboard/kpis", { params: { scenario } }),
};

export default api;
